import { BookUser, BrainCircuit, BriefcaseBusiness, Gavel, MessageSquareText, ScanSearch, UserRound } from 'lucide-react';
import { CASE_001 } from '../case/case001';
import { useGame } from '../game/GameContext';
import { isTrialReady } from '../game/reducer';
import { BrandMark } from './BrandMark';

const CHAPTERS = [
  ['intake', 'Intake'],
  ['scene-lockdown', 'Lockdown'],
  ['evidence-sweep', 'Evidence'],
  ['witness-interviews', 'Witnesses'],
  ['tribunal', 'Tribunal'],
  ['reconstruction', 'Truth'],
  ['verdict', 'Verdict'],
] as const;

export function GameHUD({ nearby }: { nearby: string | null }) {
  const { state, dispatch } = useGame();
  const found = state.discoveredEvidenceIds.length;
  const physicalTotal = CASE_001.evidence.length;
  const chapterIndex = CHAPTERS.findIndex(([id]) => id === state.chapter);
  const ready = isTrialReady(state);
  const activeAgent = state.yuragi.agents[state.activeCharacterId ?? 'lyra'];
  const objective = state.phase === 'investigation'
    ? state.chapter === 'scene-lockdown'
      ? 'Secure the room and locate the first physical break'
      : state.chapter === 'evidence-sweep'
        ? 'Preserve six independent records'
        : 'Question three witnesses before filing for trial'
    : 'Break the state timeline under oath';

  return (
    <div className="hud screen-layer screen-layer--passthrough">
      <header className="hud__top hud-interactive">
        <BrandMark compact />
        <div className="hud__case"><span>{CASE_001.id}</span><strong>{CASE_001.title}</strong></div>
        <div className="hud__resources">
          <div><span>Credibility</span><b>{Array.from({ length: 3 }, (_, index) => <i key={index} className={index < state.credibility ? 'is-full' : ''} />)}</b></div>
          <div><span>Focus</span><strong>{state.focus.toString().padStart(2, '0')}</strong></div>
        </div>
      </header>

      <div className="chapter-rail hud-interactive" aria-label="Case chapters">
        {CHAPTERS.map(([id, label], index) => (
          <div key={id} className={index === chapterIndex ? 'is-active' : index < chapterIndex ? 'is-complete' : ''}>
            <i>{index < chapterIndex ? '✓' : (index + 1).toString().padStart(2, '0')}</i>
            <span>{label}</span>
          </div>
        ))}
      </div>

      <aside className="hud__objective">
        <span>ACTIVE OBJECTIVE</span>
        <strong>{objective}</strong>
        <small>{found}/{physicalTotal} records · {state.questionedCharacterIds.length}/4 witnesses</small>
        <div><i style={{ width: `${(found / physicalTotal) * 100}%` }} /></div>
      </aside>

      {(state.phase === 'investigation' || (state.phase === 'trial' && state.overlay === 'none')) && (
        <div className={`interaction-reticle ${nearby ? 'is-near' : ''}`}>
          <span />
          {nearby && <p><kbd>E</kbd>{nearby}</p>}
        </div>
      )}

      <nav className="hud__tools hud-interactive" aria-label="Case tools">
        <button onClick={() => dispatch({ type: 'OPEN_DOSSIER' })}><BookUser /><span>Dossier</span><kbd>Tab</kbd></button>
        <button onClick={() => dispatch({ type: 'VIEW_EVIDENCE', evidenceId: state.discoveredEvidenceIds.at(-1) ?? 'charge-sheet' })}><BriefcaseBusiness /><span>Evidence</span><kbd>Q</kbd></button>
        <button onClick={() => dispatch({ type: 'OPEN_PROFILE' })}><UserRound /><span>{state.profile.callsign}</span><kbd>LV {state.profile.level}</kbd></button>
        {state.phase === 'investigation' && (
          <button className={`hud__trial ${ready ? 'is-ready' : 'is-locked'}`} onClick={() => dispatch({ type: 'START_TRIAL' })}>
            <Gavel /><span>Convene tribunal</span><small>{ready ? 'Record ready' : 'Requires 6 records + 3 witnesses'}</small>
          </button>
        )}
        {state.phase === 'trial' && (
          <>
            {state.overlay === 'none' && (
              <button onClick={() => dispatch({ type: 'START_DIALOGUE', characterId: state.activeCharacterId ?? 'lyra' })}>
                <MessageSquareText /><span>Resume testimony</span><kbd>E</kbd>
              </button>
            )}
            <button className="hud__trial" onClick={() => dispatch({ type: 'OPEN_THEORY' })}>
              <ScanSearch /><span>Reconstruct truth</span><small>{state.contradictionsFound.length}/5 contradictions</small>
            </button>
          </>
        )}
      </nav>

      <aside className="yuragi-pulse">
        <BrainCircuit />
        <div><span>YURAGI LIVE · BLOCK {state.yuragi.height}</span><strong>{activeAgent.decision} / {activeAgent.stance}</strong></div>
        <i><b style={{ width: `${Math.round(state.yuragi.syncOrder * 100)}%` }} /></i>
        <small>HERD SYNC {Math.round(state.yuragi.syncOrder * 100)}%</small>
      </aside>

      {state.phase === 'investigation' && (
        <div className="hud__controls"><span><kbd>WASD</kbd> Move</span><span><kbd>Mouse</kbd> Look</span><span><kbd>Shift</kbd> Move fast</span><span><kbd>F</kbd> Fullscreen</span></div>
      )}
      {state.phase === 'trial' && !state.courtEvent && (
        <div className="hud__controls hud__controls--court"><span><kbd>WASD</kbd> Walk court</span><span><kbd>Mouse</kbd> Look</span><span><kbd>E</kbd> Question</span><span><kbd>Esc</kbd> Leave testimony</span></div>
      )}
    </div>
  );
}
